/**
 * WeatherWidget displays flight day weather information for rooftop inspections.
 *
 * Shows:
 * - Day of week and date of flight
 * - Temperature at 1pm and 10pm on flight day
 * - Precipitation amount on flight day (if any)
 * - Weather conditions for the 5 days prior to the flight
 *
 * Uses Open-Meteo free weather API (no API key required).
 * Requires kestrelModel.data.origin coordinates and kestrelModel.data.scanDate.
 * Does not render if any required data is missing.
 */
import { Cloud, CloudRain, CloudSnow, CloudSun, Sun } from 'lucide-react'
import React, { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'

const WeatherContainer = styled.div`
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.forecast.background3};
  border: 1px solid ${({ theme }) => theme.forecast.border};
  border-radius: 0.5rem;
  padding: 0.75rem;
  color: ${({ theme }) => theme.forecast.activeText};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`

const FlightDate = styled.div`
  font-size: 0.8rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid ${({ theme }) => theme.forecast.border};
  color: ${({ theme }) => theme.forecast.activeText};
  line-height: 1.2;
`

const TemperatureSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  margin-bottom: 0.5rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid ${({ theme }) => theme.forecast.border};
`

const TempRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.7rem;
  line-height: 1.2;
`

const TempLabel = styled.span`
  color: ${({ theme }) => theme.forecast.defaultText};
`

const TempValue = styled.span`
  font-weight: 600;
  font-size: 0.8rem;
  color: ${({ theme }) => theme.forecast.activeText};
`

const PriorWeatherSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`

const WeatherDays = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 0.15rem;
`

const WeatherDay = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.15rem;
  flex: 1;
`

const DayLabel = styled.div`
  font-size: 0.55rem;
  color: ${({ theme }) => theme.forecast.defaultText};
  text-transform: uppercase;
  line-height: 1;
`

const WeatherIcon = styled.div`
  color: ${({ theme }) => theme.forecast.activeText};
  display: flex;
  align-items: center;
  justify-content: center;
`

const PrecipitationText = styled.div`
  font-size: 0.65rem;
  color: ${({ theme }) => theme.forecast.defaultText};
  line-height: 1;
  text-align: center;
  margin-top: 0.15rem;
`

const LoadingText = styled.div`
  color: ${({ theme }) => theme.forecast.defaultText};
  font-size: 0.75rem;
  text-align: center;
  padding: 0.75rem 0;
`

const ErrorText = styled.div`
  color: #ef4444;
  font-size: 0.75rem;
  text-align: center;
  padding: 0.75rem 0;
`

interface WeatherData {
  flightDate: Date
  temp1pm: number
  temp10pm: number
  priorDays: Array<{
    date: Date
    weatherCode: number
    precipitationInches: number
  }>
}

interface WeatherWidgetProps {
  flightDate?: Date | number
  latitude?: number
  longitude?: number
}

const getWeatherIcon = (weatherCode: number) => {
  if (weatherCode === 0) return <Sun size={16} />
  if (weatherCode === 1 || weatherCode === 2) return <CloudSun size={16} />
  if (weatherCode === 3) return <Cloud size={16} />
  if (weatherCode >= 51 && weatherCode <= 67) return <CloudRain size={16} />
  if (weatherCode >= 71 && weatherCode <= 77) return <CloudSnow size={16} />
  if (weatherCode >= 80 && weatherCode <= 82) return <CloudRain size={16} />
  return <Cloud size={16} />
}

const getDayOfWeek = (date: Date): string => {
  const days = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
  ]
  return days[date.getDay()]
}

const formatDate = (date: Date): string => {
  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ]
  return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`
}

const getShortDayName = (date: Date): string => {
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
  return days[date.getDay()]
}

export const WeatherWidget: React.FC<WeatherWidgetProps> = ({
  flightDate,
  latitude,
  longitude,
}) => {
  const [weatherData, setWeatherData] = useState<WeatherData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Stabilize the flightDate to prevent infinite loops
  const stableFlightDate = useMemo(() => {
    if (!flightDate) return null
    return typeof flightDate === 'number' ? flightDate : flightDate.getTime()
  }, [flightDate])

  useEffect(() => {
    // Don't fetch if required data is missing
    if (!latitude || !longitude || !stableFlightDate) {
      return
    }
    const fetchWeather = async () => {
      try {
        setLoading(true)
        setError(null)

        const lat = latitude
        const lon = longitude
        // Use 'auto' to automatically detect timezone from coordinates
        const timezone = 'auto'

        // Convert timestamp to date string using UTC to avoid timezone shifts
        const flightDate = new Date(stableFlightDate)
        const year = flightDate.getUTCFullYear()
        const month = String(flightDate.getUTCMonth() + 1).padStart(2, '0')
        const day = String(flightDate.getUTCDate()).padStart(2, '0')
        const localDateString = `${year}-${month}-${day}`

        // Calculate start date (5 days before)
        const startDateObj = new Date(flightDate)
        startDateObj.setUTCDate(startDateObj.getUTCDate() - 5)
        const startYear = startDateObj.getUTCFullYear()
        const startMonth = String(startDateObj.getUTCMonth() + 1).padStart(
          2,
          '0'
        )
        const startDay = String(startDateObj.getUTCDate()).padStart(2, '0')
        const startDateString = `${startYear}-${startMonth}-${startDay}`

        // Create cache key based on date and location
        const cacheKey = `weather_${localDateString}_${lat.toFixed(2)}_${lon.toFixed(2)}`

        // Check cache first (cache for 1 hour)
        try {
          const cached = localStorage.getItem(cacheKey)
          if (cached) {
            const { data: cachedData, timestamp } = JSON.parse(cached)
            const cacheAge = Date.now() - timestamp
            if (cacheAge < 60 * 60 * 1000) {
              // 1 hour
              // Convert cached date strings back to Date objects
              const restoredData = {
                ...cachedData,
                flightDate: new Date(cachedData.flightDate),
                priorDays: cachedData.priorDays.map(
                  (day: {
                    date: string | Date
                    weatherCode: number
                    precipitationInches: number
                  }) => ({
                    ...day,
                    date: new Date(day.date),
                  })
                ),
              }
              setWeatherData(restoredData)
              setLoading(false)
              return
            }
          }
        } catch {
          // Ignore cache errors
        }

        // Determine if we need historical data (for past dates) or forecast data (for future dates)
        const isHistorical = flightDate < new Date()
        const apiEndpoint = isHistorical
          ? 'https://archive-api.open-meteo.com/v1/archive'
          : 'https://api.open-meteo.com/v1/forecast'

        const apiUrl = `${apiEndpoint}?latitude=${lat}&longitude=${lon}&hourly=temperature_2m,weather_code,precipitation&temperature_unit=fahrenheit&precipitation_unit=inch&start_date=${startDateString}&end_date=${localDateString}&timezone=${timezone}`

        const response = await fetch(apiUrl)

        const data = await response.json()

        // Check for API errors (including rate limits)
        if (data.error) {
          throw new Error(data.reason || 'Failed to fetch weather data')
        }

        if (!response.ok) {
          throw new Error('Failed to fetch weather data')
        }

        const getTemperatureAtHour = (
          dateString: string,
          hour: number
        ): number => {
          // Format: YYYY-MM-DDTHH:00
          const targetTimeString = `${dateString}T${String(hour).padStart(2, '0')}:00`

          let index = data.hourly.time.findIndex((time: string) =>
            time.startsWith(targetTimeString)
          )

          // If exact hour not found, try to find closest available hour within +/- 2 hours
          if (index < 0) {
            for (let offset = 1; offset <= 2; offset++) {
              // Try hours after
              const laterHour = hour + offset
              const laterTimeString = `${dateString}T${String(laterHour).padStart(2, '0')}:00`
              index = data.hourly.time.findIndex((time: string) =>
                time.startsWith(laterTimeString)
              )
              if (index >= 0) break

              // Try hours before
              const earlierHour = hour - offset
              if (earlierHour >= 0) {
                const earlierTimeString = `${dateString}T${String(earlierHour).padStart(2, '0')}:00`
                index = data.hourly.time.findIndex((time: string) =>
                  time.startsWith(earlierTimeString)
                )
                if (index >= 0) break
              }
            }
          }

          const temp =
            index >= 0 ? Math.round(data.hourly.temperature_2m[index]) : 0

          return temp
        }

        const getWeatherCodeForDay = (dateString: string): number => {
          const dayIndices = data.hourly.time
            .map((time: string, index: number) => ({ time, index }))
            .filter((item: { time: string }) =>
              item.time.startsWith(dateString)
            )
            .map((item: { index: number }) => item.index)

          if (dayIndices.length === 0) return 0

          const weatherCodes = dayIndices.map(
            (i: number) => data.hourly.weather_code[i]
          )
          const avgCode = Math.round(
            weatherCodes.reduce((a: number, b: number) => a + b, 0) /
              weatherCodes.length
          )
          return avgCode
        }

        const getPrecipitationForDay = (dateString: string): number => {
          const dayIndices = data.hourly.time
            .map((time: string, index: number) => ({ time, index }))
            .filter((item: { time: string }) =>
              item.time.startsWith(dateString)
            )
            .map((item: { index: number }) => item.index)

          if (dayIndices.length === 0) return 0

          const precipitationValues = dayIndices.map(
            (i: number) => data.hourly.precipitation[i] || 0
          )
          const totalPrecipitation = precipitationValues.reduce(
            (a: number, b: number) => a + b,
            0
          )
          return Math.round(totalPrecipitation * 100) / 100
        }

        const priorDays = []
        for (let i = 5; i >= 1; i--) {
          const priorDate = new Date(flightDate)
          priorDate.setDate(priorDate.getDate() - i)
          const priorDateString = priorDate.toLocaleDateString('en-CA')
          priorDays.push({
            date: priorDate,
            weatherCode: getWeatherCodeForDay(priorDateString),
            precipitationInches: getPrecipitationForDay(priorDateString),
          })
        }

        const weatherResult = {
          flightDate: flightDate,
          temp1pm: getTemperatureAtHour(localDateString, 13),
          temp10pm: getTemperatureAtHour(localDateString, 22),
          priorDays,
        }

        setWeatherData(weatherResult)

        // Cache the result
        try {
          localStorage.setItem(
            cacheKey,
            JSON.stringify({
              data: weatherResult,
              timestamp: Date.now(),
            })
          )
        } catch {
          // Ignore cache storage errors (e.g., quota exceeded)
        }
      } catch (err) {
        console.error('Error fetching weather data:', err)
        setError(
          err instanceof Error ? err.message : 'Failed to load weather data'
        )
      } finally {
        setLoading(false)
      }
    }

    fetchWeather()
  }, [stableFlightDate, latitude, longitude])

  // Don't render if required data is missing
  if (!latitude || !longitude || !flightDate) {
    return null
  }

  if (loading) {
    return (
      <WeatherContainer>
        <LoadingText>Loading weather data...</LoadingText>
      </WeatherContainer>
    )
  }

  if (error) {
    const isRateLimit = error.includes('request limit exceeded')
    return (
      <WeatherContainer>
        <ErrorText>
          {isRateLimit
            ? 'Weather API rate limit reached. Data will be available in ~1 hour.'
            : error}
        </ErrorText>
      </WeatherContainer>
    )
  }

  if (!weatherData) {
    return null
  }

  return (
    <WeatherContainer>
      <FlightDate>
        {getDayOfWeek(weatherData.flightDate)},{' '}
        {formatDate(weatherData.flightDate)}
      </FlightDate>

      <TemperatureSection>
        <TempRow>
          <TempLabel>1:00 PM</TempLabel>
          <TempValue>{weatherData.temp1pm}°F</TempValue>
        </TempRow>
        <TempRow>
          <TempLabel>10:00 PM</TempLabel>
          <TempValue>{weatherData.temp10pm}°F</TempValue>
        </TempRow>
      </TemperatureSection>

      <PriorWeatherSection>
        <WeatherDays>
          {weatherData.priorDays.map((day, index) => (
            <WeatherDay key={index}>
              <DayLabel>{getShortDayName(day.date)}</DayLabel>
              <WeatherIcon>{getWeatherIcon(day.weatherCode)}</WeatherIcon>
              {day.precipitationInches > 0 && (
                <PrecipitationText>
                  {day.precipitationInches}&quot;
                </PrecipitationText>
              )}
            </WeatherDay>
          ))}
        </WeatherDays>
      </PriorWeatherSection>
    </WeatherContainer>
  )
}
